/**
 * VARIATION ORDERS — the register, the editor and one variation in detail.
 *
 * WHY THIS SCREEN HAD TO EXIST
 *
 * rpc_mb_submit refuses a measurement that goes past fn_wo_line_allowed, and
 * the only lawful way past that ceiling is an APPROVED variation. Until this
 * screen there was no way to raise one — so a site that had over-run could not
 * measure the work it had actually done, and therefore could not bill it. The
 * register listed variations that nobody could create.
 *
 * THE TWO KINDS OF LINE, AND WHY THEY LOOK DIFFERENT
 *
 *   VARY A LINE THAT IS ALREADY ON THE ORDER
 *     More of it, or less of it. The rate is the ORDER'S rate and is shown
 *     read-only, because every bill is rate x quantity taken from the work
 *     order line: a variation priced differently would have three people
 *     approve one figure and the contractor paid another. The panel shows what
 *     was ordered, what is already allowed, and what has been certified, so an
 *     omission that would fall below work already signed off is refused here
 *     rather than by a trigger three clicks later.
 *
 *   ADD AN ITEM NOBODY FORESAW
 *     Chosen from the schedule of rates, exactly as on the work order builder,
 *     with its own SAC and its own rate. On approval it becomes a real line on
 *     the order at quantity ZERO — nothing was ordered, the whole quantity is
 *     allowed by the variation — so the order reads "Ordered 0, Allowed 40" and
 *     anybody can see at a glance that the item arrived by variation.
 *
 *     A measured item, and only a measured item. An area rate needs its area
 *     basis, a lumpsum its milestone and a day rate its trade and headcount,
 *     and a variation line carries none of those; the database refuses the
 *     other three in those words (fn_vo_line_guard, migration 0135).
 *
 * WHAT THE SCREEN DOES NOT DO
 *
 * It does not change the contract value. contract_value is what was SIGNED and
 * stays that way for ever — the advance is a percentage of it and the printed
 * order carries it. What the order is worth NOW is derived and shown beside it,
 * from v_wo_variation_position. Two questions, two figures, and only one of
 * them stored so they cannot drift apart.
 *
 * Every refusal here is also enforced in the database. If a button is missing
 * the server would have said no anyway; the button is the courtesy.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Ban, Clock, FileCheck2, FileDown, ListChecks, Minus,
  Plus, Search, Send, Trash2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { usePeople, useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, qty, rupees } from '@/lib/format'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, SearchInput,
  Select, Stat, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { AuditTrail } from '@/components/docs/shared'
import { ApprovalChain } from '../components/ApprovalChain'
import { VoSignedCopy } from '../components/VoSignedCopy'
import { serviceCan } from '../nav'

const n = (v: unknown) => Number(v ?? 0) || 0
const num = (v: string) => Number(v) || 0

/** The statuses a variation may still be raised against. Same list as rpc_vo_raise. */
const VARIABLE = ['APPROVED', 'VENDIN', 'VENDACK', 'PARTIALLY_RECEIVED']
/** The statuses in which a variation is still the raiser's to change. */
const OPEN = ['DRAFT', 'RETURNED']

interface VoRow {
  id: string; wo_id: string; site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null
  wo_title: string; wo_status: string
  wo_ordered_value: number; wo_revised_value: number
  variation_no: number; reason: string; variation_value: number
  status: string; current_step: number; date_created: string
  created_by: string | null; creator_email: string | null
  approver_email: string | null; approval_date: string | null
  rejection_reason: string | null
  line_count: number; additions: number; omissions: number
  pct_of_order: number | null
  /** 0138. A count, never a path — the register is read by people who may
   *  not open the scan, and "has it come back" has one answer for everybody. */
  signed_copies: number
}

interface VoLine {
  id: string; variation_id: string; wo_line_id: string | null
  sl_no: number; work_item_id: string | null; sac_code: string | null
  pricing_basis: string; description: string; scope_text: string | null
  uom: string; qty_change: number; rate: number; amount: number
  gst_rate_percent: number; remarks: string | null
}

interface WoLineRow {
  wo_line_id: string; wo_id: string; sl_no: number; pricing_basis: string
  description: string; uom: string; rate: number
  qty_ordered: number; qty_allowed: number; qty_certified: number
}

// ===========================================================================
// THE REGISTER
// ===========================================================================
export function VariationList() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [term, setTerm] = useState('')
  const { sort, toggle } = useSort('date_created', 'desc')
  const [raising, setRaising] = useState(false)
  const [woId, setWoId] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const mayRaise = serviceCan(profile?.role, 'raiseVariation')

  const { data: rows, loading, refresh } = useQuery<VoRow>(
    'v_variation_register',
    () => supabase.from('v_variation_register').select('*'),
    [], { cacheable: false })

  /** The orders a variation may be raised against, and which of them already have one. */
  const { data: orders } = useQuery<any>(
    'vo_variable_orders',
    () => supabase.from('v_work_order_register').select('*').in('status', VARIABLE)
      .order('date_created', { ascending: false }),
    [], { cacheable: false, enabled: mayRaise })
  const { data: position } = useQuery<any>(
    'vo_position_all',
    () => supabase.from('v_wo_variation_position').select('*'),
    [], { cacheable: false, idField: 'wo_id', enabled: mayRaise })

  const openOn = useMemo(() => {
    const m: Record<string, string> = {}
    for (const p of position as any[]) if (p.open_variation_id) m[p.wo_id] = p.open_variation_id
    return m
  }, [position])

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    return sortRows(rows.filter((r) => !t
      || [r.id, r.wo_id, r.reason, r.vendor_name, r.site_name]
        .some((v) => String(v ?? '').toLowerCase().includes(t))),
      sort, { variation_value: 'number', date_created: 'date', variation_no: 'number',
              line_count: 'number' })
  }, [rows, term, sort])

  const added = rows.filter((r) => r.status === 'APPROVED')
    .reduce((a, r) => a + n(r.variation_value), 0)
  const inChain = rows.filter((r) => r.status === 'SUBMITTED').length
  const drafts = rows.filter((r) => OPEN.includes(r.status)).length

  async function raise() {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_vo_raise',
        { p_wo_id: woId, p_reason: reason.trim() })
      if (error) throw new Error(error.message)
      const id = String(data)
      toast.success(`${id} raised. Put the lines on it, then submit.`)
      setRaising(false); setWoId(''); setReason('')
      navigate(`/service/variations/${id}/edit`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not raise it')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the variations…" />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Variation Orders</h1>
          <p className="text-sm text-slate-500">
            {rows.length} raised · {rupees(added)} added to contract values by approved variations
          </p>
        </div>
        {mayRaise && (
          <button className="btn-primary" onClick={() => setRaising(true)}>
            <Plus className="h-4 w-4" /> Raise a variation
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Added by approved variations" value={rupees(added)}
                sub="on top of the signed contract values"
                icon={<ListChecks className="h-5 w-5" />} />
          <Stat label="In the chain" value={inChain}
                sub={inChain ? 'waiting on an approver' : 'nothing awaiting approval'}
                tone={inChain ? 'warn' : 'default'} />
          <Stat label="Still being written" value={drafts}
                sub={drafts ? 'draft or returned, not yet submitted' : 'none in draft'} />
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <ListChecks className="mr-1 inline h-3.5 w-3.5" />
        A variation is the only way a measurement can go past the quantity that was
        ordered. It runs its own two-stage chain — the Project Manager of the site,
        then the Project Head, whose approval is final — and it can take work away as
        well as add it. The signed contract value never changes; what the order is
        worth now is shown beside it.
      </div>

      <Card title={null}>
        <SearchInput value={term} onChange={setTerm} className="mb-3"
                     placeholder="Variation, work order, contractor, site or reason…" />
        {shown.length === 0 ? (
          <EmptyState icon={<ListChecks className="h-8 w-8" />}
                      title={term ? 'Nothing matches' : 'No variations'}
                      message={term
                        ? `${rows.length} variation${rows.length === 1 ? ' is' : 's are'} hidden by that search.`
                        : 'Nothing has needed one yet, which is a good sign.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Variation</Th>
                  <Th id="wo_id" sort={sort} onSort={toggle}>Work order</Th>
                  <Th id="variation_no" sort={sort} onSort={toggle} align="right">No.</Th>
                  <th>Reason</th>
                  <Th id="line_count" sort={sort} onSort={toggle} align="right">Lines</Th>
                  <Th id="variation_value" sort={sort} onSort={toggle} align="right">Value</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  {/* Sortable on purpose: sorting by it puts every approved
                      variation still waiting for its signed sheet at one end of
                      the list, which is the question Procurement asks of this
                      screen on a Monday morning. */}
                  <Th id="signed_copies" sort={sort} onSort={toggle}>Signed back</Th>
                  <Th id="date_created" sort={sort} onSort={toggle}>Raised</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/variations/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {r.site_name} · {r.vendor_name}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="text-amber-700 hover:underline">{r.wo_id}</Link>
                    </td>
                    <td className="num">{r.variation_no}</td>
                    <td className="max-w-[280px] truncate">{r.reason}</td>
                    <td className="num whitespace-nowrap text-xs">
                      {r.line_count}
                      {n(r.omissions) > 0 && (
                        <span className="ml-1 text-emerald-700">({r.omissions} off)</span>
                      )}
                    </td>
                    <td className={n(r.variation_value) < 0
                      ? 'num whitespace-nowrap text-emerald-700'
                      : 'num whitespace-nowrap'}>
                      {rupees(r.variation_value)}
                      {r.pct_of_order != null && (
                        <span className="block text-[11px] text-slate-400">
                          {n(r.pct_of_order)}% of order
                        </span>
                      )}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                    {/* Nothing at all for a variation nobody has approved yet:
                        there is no signed copy of a document the Project Head
                        has not signed, and an "awaited" against a draft would
                        be chasing somebody for a thing that cannot exist. */}
                    <td className="whitespace-nowrap text-xs">
                      {r.status !== 'APPROVED' ? <span className="text-slate-300">—</span>
                        : n(r.signed_copies) > 0
                          ? <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                              <FileCheck2 className="h-3.5 w-3.5" /> On file
                            </span>
                          : <span className="inline-flex items-center gap-1 text-amber-700">
                              <Clock className="h-3.5 w-3.5" /> Awaited
                            </span>}
                    </td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.date_created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------- raising one ---------- */}
      <Modal open={raising} onClose={() => setRaising(false)}
             title="Raise a variation"
             subtitle="Against a live order. The lines go on the next screen."
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setRaising(false)}>Cancel</button>
                 <button className="btn-primary"
                         disabled={busy || !woId || reason.trim().length < 5}
                         onClick={() => void raise()}>
                   Raise it
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Field label="Work order" required
                 hint="Only orders that have been approved and are still running. A draft is edited, not varied.">
            <Select value={woId} onChange={(e) => setWoId(e.target.value)}
                    placeholder={(orders as any[]).length
                      ? 'Choose…' : 'No live work order at your sites yet'}
                    options={(orders as any[]).map((o) => ({
                      value: o.id,
                      label: `${o.id} — ${o.vendor_name} — ${o.title}`
                             + (openOn[o.id] ? `  (${openOn[o.id]} already open)` : ''),
                    }))} />
          </Field>
          {woId && openOn[woId] && (
            <Alert tone="warning" title="That order already has a variation in progress">
              <Link to={`/service/variations/${openOn[woId]}`}
                    className="font-semibold underline">{openOn[woId]}</Link> is still
              open on it. Finish it or cancel it first — one at a time is how the same
              extra work avoids being approved twice.
            </Alert>
          )}
          <Field label="Why is it needed" required
                 hint="The first thing every approver reads. Say what changed on site and who asked for it.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Rock encountered below 1.5m in the footing pits; excavation quantity rises and a rock-cutting item is added, agreed at site with the PM on 12-09-2026." />
          </Field>
        </div>
      </Modal>
    </div>
  )
}

// ===========================================================================
// THE EDITOR
// ===========================================================================

/**
 * THE DRAFT, WHILE YOU ARE AWAY CHOOSING A WORK ITEM.
 *
 * The same problem the work order builder has, solved the same way and for the
 * same reason: 562 items in a <select> is a scroll bar with no search, so
 * choosing one is a full-page trip to the schedule of rates and a half-written
 * variation must survive it. A module-level variable, because both screens are
 * in the same lazily-loaded Service chunk and a client-side navigation between
 * them does not reload this module.
 *
 * It does NOT survive a refresh or a closed tab, which is correct — but unlike
 * the builder, a variation always exists on the server before this screen opens
 * (rpc_vo_raise makes it), so the worst a lost draft costs is re-typing the
 * lines, not the document.
 */
interface Row {
  key: string
  kind: 'EXISTING' | 'NEW'
  wo_line_id: string
  work_item_id: string
  sac_code: string
  description: string
  scope_text: string
  uom: string
  qty_change: string
  rate: string
  gst_rate_percent: string
  remarks: string
}

const blankRow = (kind: 'EXISTING' | 'NEW'): Row => ({
  key: Math.random().toString(36).slice(2),
  kind, wo_line_id: '', work_item_id: '', sac_code: '', description: '',
  scope_text: '', uom: '', qty_change: '', rate: '', gst_rate_percent: '18', remarks: '',
})

interface Parked { voId: string; reason: string; rows: Row[] }
let parked: Parked | null = null

function unpark(voId: string, returning: boolean): Parked | null {
  if (!returning || !parked || parked.voId !== voId) { parked = null; return null }
  return parked
}

export function VariationEditor() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const pickedId = params.get('picked')
  const pickedRow = params.get('line')
  const resuming = params.get('resume') === '1'
  /** Arrived from a measurement page that over-ran. See the effect below. */
  const fromMb = params.get('from')
  const [resumed] = useState<Parked | null>(
    () => unpark(id, Boolean((pickedId && pickedRow) || resuming)))

  const [reason, setReason] = useState(resumed?.reason ?? '')
  const [rows, setRows] = useState<Row[]>(resumed?.rows ?? [])
  const [loaded, setLoaded] = useState(Boolean(resumed))
  const [woId, setWoId] = useState('')
  const [status, setStatus] = useState('DRAFT')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [prefilled, setPrefilled] = useState(false)
  /** The measurement page these lines were filled in from, once they have been. */
  const [filledFrom, setFilledFrom] = useState<string | null>(null)

  const { data: items } = useQuery<any>('work_items',
    () => supabase.from('work_items').select('*').eq('is_active', true).order('item_name'),
    [], { cacheable: false })
  const { data: sacs } = useQuery<any>('sac_codes',
    () => supabase.from('sac_codes').select('code, description, gst_rate_percent')
      .eq('is_active', true).order('code'),
    [], { cacheable: false, idField: 'code' })
  const { data: woLines } = useQuery<WoLineRow>(
    `vo_wolines_${woId}`,
    () => supabase.from('v_wo_line_progress').select('*')
      .eq('wo_id', woId || '__none__').order('sl_no'),
    [woId], { cacheable: false, idField: 'wo_line_id', enabled: Boolean(woId) })

  // ---- load the variation, unless we have just come back from the picker ---
  useEffect(() => {
    if (!id) return
    void (async () => {
      const { data: v } = await supabase.from('wo_variations').select('*')
        .eq('id', id).maybeSingle()
      if (!v) { setLoaded(true); return }
      setWoId(v.wo_id); setStatus(String(v.status))
      if (resumed) { setLoaded(true); return }
      setReason(v.reason ?? '')
      const { data: ls } = await supabase.from('wo_variation_lines').select('*')
        .eq('variation_id', id).order('sl_no')
      setRows((ls ?? []).map((l: any) => ({
        key: l.id,
        kind: l.wo_line_id ? 'EXISTING' : 'NEW',
        wo_line_id: l.wo_line_id ?? '',
        work_item_id: l.work_item_id ?? '',
        sac_code: l.sac_code ?? '',
        description: l.description ?? '',
        scope_text: l.scope_text ?? '',
        uom: l.uom ?? '',
        qty_change: String(l.qty_change ?? ''),
        rate: String(l.rate ?? ''),
        gst_rate_percent: String(l.gst_rate_percent ?? 18),
        remarks: l.remarks ?? '',
      })))
      setLoaded(true)
    })()
  }, [id, resumed])

  // ---- the item chosen on the schedule of rates ---------------------------
  //
  // Waits for the work items to arrive: applying a pick against an empty list
  // would silently blank the line instead of filling it. Same guard, and the
  // same reason, as the work order builder.
  useEffect(() => {
    if (!pickedId && !resuming) return
    if (pickedId && !(items as any[]).length) return
    if (pickedId && pickedRow) {
      const it = (items as any[]).find((x) => x.id === pickedId)
      if (it) {
        setRows((rs) => rs.map((r) => (r.key === pickedRow ? {
          ...r,
          work_item_id: it.id,
          description: it.item_name ?? '',
          uom: it.uom ?? '',
          sac_code: it.sac_code ?? '',
          gst_rate_percent: String(it.gst_rate_percent ?? 18),
          rate: it.standard_rate ? String(it.standard_rate) : r.rate,
        } : r)))
      }
    }
    parked = null                    // the round trip is over; nothing to keep
    setParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedId, pickedRow, resuming, items])

  const lineOf = useMemo(() => {
    const m: Record<string, WoLineRow> = {}
    for (const l of woLines as WoLineRow[]) m[l.wo_line_id] = l
    return m
  }, [woLines])

  /**
   * THE OVER-RUN, CARRIED ACROSS FROM THE MEASUREMENT PAGE.
   *
   * A buyer who gets here from "this page goes past what was ordered" already
   * knows exactly what the variation has to say: the same lines, the same
   * shortfall, to the same three decimal places the certification trigger will
   * use. Making him re-key it is how the quantity that decides a payment gets
   * mistyped.
   *
   * fn_mb_overrun is asked again here rather than the numbers being passed in
   * the URL, for the reason the measurement screen gives for calling it in the
   * first place: it is the SAME function the certification trigger runs, so the
   * variation is raised for precisely the quantity that was refused. A number
   * carried in a query string is a number that can be edited in the address bar.
   *
   * It only ADDS. A line already on the variation for that order line is left
   * exactly as the buyer left it — arriving here twice must not double a
   * quantity, and his own figure beats a computed one.
   */
  useEffect(() => {
    if (!fromMb || prefilled || !loaded) return
    if (!(woLines as WoLineRow[]).length) return
    let dead = false
    void (async () => {
      const { data, error } = await supabase.rpc('fn_mb_overrun', { p_mb_id: fromMb })
      if (dead) return
      if (error) { toast.error(`Could not read the over-run on ${fromMb}.`) }
      const over = (data ?? []) as any[]
      let added = 0
      setRows((rs) => {
        const have = new Set(rs.filter((r) => r.kind === 'EXISTING').map((r) => r.wo_line_id))
        const add: Row[] = []
        for (const o of over) {
          if (have.has(o.wo_line_id)) continue
          const l = (woLines as WoLineRow[]).find((x) => x.wo_line_id === o.wo_line_id)
          if (!l) continue
          add.push({
            ...blankRow('EXISTING'),
            wo_line_id: o.wo_line_id,
            description: l.description,
            uom: l.uom,
            rate: String(l.rate),
            qty_change: String(Number(o.over_by) || 0),
            remarks: `To cover the over-run measured on ${fromMb}`,
          })
        }
        added = add.length
        return add.length ? [...rs, ...add] : rs
      })
      setPrefilled(true)
      if (added > 0) {
        setFilledFrom(fromMb)
        toast.success(`${added} line${added === 1 ? '' : 's'} filled in from ${fromMb}. `
                      + 'Check the quantities, then submit.')
      }
      const next = new URLSearchParams(params)
      next.delete('from')
      setParams(next, { replace: true })
    })()
    return () => { dead = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMb, prefilled, loaded, woLines])

  function setRow(key: string, patch: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  /** Picking an order line fixes the description, the unit and — deliberately — the rate. */
  function pickWoLine(key: string, woLineId: string) {
    const l = lineOf[woLineId]
    setRow(key, {
      wo_line_id: woLineId,
      description: l?.description ?? '',
      uom: l?.uom ?? '',
      rate: l ? String(l.rate) : '',
    })
  }

  function pickSac(key: string, code: string) {
    const rate = (sacs as any[]).find((x) => x.code === code)?.gst_rate_percent
    setRow(key, { sac_code: code, ...(rate == null ? {} : { gst_rate_percent: String(rate) }) })
  }

  function goToPicker(rowKey: string) {
    parked = { voId: id, reason, rows }
    const back = encodeURIComponent(location.pathname)
    navigate(`/service/work-items?pick=${encodeURIComponent(rowKey)}&back=${back}`)
  }

  const value = useMemo(
    () => rows.reduce((a, r) => a + Math.round(num(r.qty_change) * num(r.rate) * 100) / 100, 0),
    [rows])

  // ---- what would stop this being saved -----------------------------------
  const problems = useMemo(() => {
    const p: string[] = []
    if (reason.trim().length < 5) p.push('Say why the variation is needed. It is the first thing every approver reads.')
    if (rows.length === 0) p.push('A variation with no lines has nothing for anybody to approve.')
    rows.forEach((r, i) => {
      const at = `Line ${i + 1}`
      const q = num(r.qty_change)
      if (num(r.rate) <= 0) p.push(`${at}: a rate is needed. Nothing is done for nothing.`)
      if (r.kind === 'EXISTING') {
        if (!r.wo_line_id) { p.push(`${at}: choose the order line being varied.`); return }
        const l = lineOf[r.wo_line_id]
        if (q === 0) p.push(`${at}: say how much more, or how much less. Zero is not a variation.`)
        if (l && Math.abs(num(r.rate) - n(l.rate)) > 0.005) {
          p.push(`${at}: that line is ordered at ${rupees(l.rate)}, and a variation has to be at the same rate — the bill is always the line's own rate times the quantity. To agree a new rate, add it as a new item.`)
        }
        if (l && q < 0 && n(l.qty_allowed) + q < n(l.qty_certified)) {
          p.push(`${at}: that would reduce the line to ${qty(n(l.qty_allowed) + q)} ${l.uom}, but ${qty(l.qty_certified)} has already been measured and certified. A variation cannot un-do work that is done.`)
        }
      } else {
        if (!r.work_item_id) p.push(`${at}: choose the work item from the schedule of rates.`)
        if (!r.sac_code) p.push(`${at}: choose a SAC code. It is printed on the order.`)
        if (!r.uom.trim()) p.push(`${at}: the unit is missing.`)
        if (q <= 0) p.push(`${at}: a new item must have a positive quantity. To take work away, vary the line that already carries it.`)
      }
    })
    const dupes = rows.filter((r) => r.kind === 'EXISTING' && r.wo_line_id)
      .map((r) => r.wo_line_id)
    if (new Set(dupes).size !== dupes.length) {
      p.push('The same order line appears twice. Put the whole change on one line — two entries against one item is how a quantity gets counted twice.')
    }
    return p
  }, [reason, rows, lineOf])

  async function save(thenSubmit: boolean) {
    setErr(null)
    if (problems.length) {
      setErr('Fix the points listed below first.')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setBusy(true)
    try {
      const { error: hErr } = await supabase.from('wo_variations')
        .update({ reason: reason.trim() }).eq('id', id)
      if (hErr) throw new Error(hErr.message)

      // Replaced wholesale. Nothing hangs off a variation that has not been
      // approved, and diffing would be a lot of code to save one DELETE.
      const { error: dErr } = await supabase.from('wo_variation_lines')
        .delete().eq('variation_id', id)
      if (dErr) throw new Error(dErr.message)

      const { error: lErr } = await supabase.from('wo_variation_lines').insert(
        rows.map((r, i) => ({
          variation_id: id,
          wo_line_id: r.kind === 'EXISTING' ? r.wo_line_id : null,
          sl_no: i + 1,
          work_item_id: r.kind === 'NEW' ? r.work_item_id : null,
          sac_code: r.kind === 'NEW' ? r.sac_code : null,
          pricing_basis: 'MEASURED_ITEM',
          description: r.description.trim(),
          scope_text: r.kind === 'NEW' ? (r.scope_text.trim() || null) : null,
          uom: r.uom.trim(),
          qty_change: num(r.qty_change),
          rate: num(r.rate),
          gst_rate_percent: num(r.gst_rate_percent),
          remarks: r.remarks.trim() || null,
        })))
      if (lErr) throw new Error(lErr.message)

      if (thenSubmit) {
        const { data, error: sErr } = await supabase.rpc('rpc_works_action', {
          p_doc_type: 'WO_VARIATION', p_doc_id: id, p_action: 'SUBMIT', p_comment: null,
        })
        if (sErr) throw new Error(sErr.message)
        toast.success(String(data ?? 'Submitted.'))
      } else {
        toast.success('Saved as a draft.')
      }
      parked = null
      navigate(`/service/variations/${id}`)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the variation')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally { setBusy(false) }
  }

  async function cancelIt(why?: string) {
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_vo_cancel',
        { p_vo_id: id, p_reason: (why ?? '').trim() })
      if (error) throw new Error(error.message)
      toast.success('Cancelled. The order is free for another variation.')
      setCancelling(false)
      navigate('/service/variations')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not cancel it')
    } finally { setBusy(false) }
  }

  if (!serviceCan(profile?.role, 'raiseVariation')) {
    return <Alert tone="warning" title="Not open to your role">
      A variation is raised by Procurement. The Project Manager of the site approves
      it first, and the Project Head has the last word.
    </Alert>
  }
  if (!loaded) return <Loading label="Reading the variation…" />
  if (!OPEN.includes(status)) {
    return (
      <div className="space-y-4">
        <Link to={`/service/variations/${id}`}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> {id}
        </Link>
        <Alert tone="warning" title={`${id} is ${status.toLowerCase()}`}>
          Its lines are settled. A variation can only be changed while it is a draft or
          while it has been returned to you. If something else has to change, raise
          another variation.
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Link to={`/service/variations/${id}`}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {id}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{id}</h1>
          <p className="text-sm text-slate-600">
            Varying <Link to={`/service/work-orders/${woId}`}
                          className="font-medium text-amber-700 hover:underline">{woId}</Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={status} />
          <button className="btn-ghost text-red-600" disabled={busy}
                  onClick={() => setCancelling(true)}>
            <Ban className="h-4 w-4" /> Cancel this variation
          </button>
        </div>
      </div>

      {err && <Alert tone="error" title="Not saved">{err}</Alert>}

      {filledFrom && (
        <Alert tone="info" title={`Filled in from ${filledFrom}`}>
          The quantities below are exactly what that measurement page went over by —
          the same arithmetic the certification trigger runs, so approving this for
          these figures is enough to let the page certify unchanged. Change them if the
          contractor is owed something different, and add any other line he is owed.
          Nothing is saved until you press Save.{' '}
          <Link to={`/service/measurements/${filledFrom}`}
                className="font-semibold underline">Open {filledFrom}</Link>
        </Alert>
      )}

      <Card title="Why">
        <Field label="Reason" required
               hint="Every approver reads this before anything else. What changed on site, who agreed it, and when.">
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Card>

      {/* ---------- the lines ---------- */}
      <Card title="What changes"
            subtitle="More or less of something already ordered, or an item nobody foresaw."
            actions={
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary btn-sm"
                        onClick={() => setRows((rs) => [...rs, blankRow('EXISTING')])}>
                  <Plus className="h-4 w-4" /> Vary an ordered line
                </button>
                <button className="btn-secondary btn-sm"
                        onClick={() => setRows((rs) => [...rs, blankRow('NEW')])}>
                  <Plus className="h-4 w-4" /> Add a new item
                </button>
              </div>
            }>
        {rows.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-500">
            Nothing on it yet. Add a line above.
          </p>
        )}

        <div className="space-y-4">
          {rows.map((r, i) => {
            const l = r.kind === 'EXISTING' ? lineOf[r.wo_line_id] : undefined
            const q = num(r.qty_change)
            const amount = Math.round(q * num(r.rate) * 100) / 100
            const floorBust = Boolean(l && q < 0 && n(l.qty_allowed) + q < n(l.qty_certified))
            return (
              <div key={r.key}
                   className={r.kind === 'NEW'
                     ? 'rounded-lg border border-sky-200 bg-sky-50/40 p-3'
                     : 'rounded-lg border border-slate-200 p-3'}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                    {i + 1}. {r.kind === 'NEW' ? 'New item, added by this variation'
                                               : 'A line already on the order'}
                  </p>
                  <button className="btn-ghost btn-sm text-red-600"
                          onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>

                {r.kind === 'EXISTING' ? (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Order line" required className="sm:col-span-2">
                      <Select value={r.wo_line_id}
                              onChange={(e) => pickWoLine(r.key, e.target.value)}
                              placeholder={(woLines as WoLineRow[]).length
                                ? 'Choose…' : 'This order has no lines'}
                              options={(woLines as WoLineRow[]).map((x) => ({
                                value: x.wo_line_id,
                                label: `${x.sl_no}. ${x.description} — ${qty(x.qty_ordered)} ${x.uom} at ${rupees(x.rate)}`,
                              }))} />
                    </Field>
                    <Field label="Change in quantity" required
                           hint="Positive adds, negative takes away.">
                      <Input type="number" step="0.001" value={r.qty_change}
                             onChange={(e) => setRow(r.key, { qty_change: e.target.value })}
                             placeholder="e.g. 250 or -40" />
                    </Field>
                    <Field label="Rate" hint="The order's own rate. It cannot change on a variation.">
                      <Input value={r.rate} readOnly disabled className="bg-slate-50" />
                    </Field>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Work item" required className="sm:col-span-2"
                           hint="From the schedule of rates, so the item and its measurement rule are the company's, not this order's.">
                      <div className="flex gap-2">
                        <Input value={r.description} readOnly
                               placeholder="Nothing chosen yet"
                               className="flex-1 bg-slate-50" />
                        <button className="btn-secondary shrink-0"
                                onClick={() => goToPicker(r.key)}>
                          <Search className="h-4 w-4" /> Choose
                        </button>
                      </div>
                    </Field>
                    <Field label="Unit" required>
                      <Input value={r.uom} onChange={(e) => setRow(r.key, { uom: e.target.value })}
                             readOnly className="bg-slate-50" />
                    </Field>
                    <Field label="SAC" required hint="Printed on the order.">
                      <Select value={r.sac_code} onChange={(e) => pickSac(r.key, e.target.value)}
                              placeholder="Choose…"
                              options={(sacs as any[]).map((s) => ({
                                value: s.code, label: `${s.code} — ${s.description}`,
                              }))} />
                    </Field>
                    <Field label="Quantity" required hint="What is being added. It cannot be negative.">
                      <Input type="number" min="0" step="0.001" value={r.qty_change}
                             onChange={(e) => setRow(r.key, { qty_change: e.target.value })} />
                    </Field>
                    <Field label="Rate" required>
                      <Input type="number" min="0" step="0.01" value={r.rate}
                             onChange={(e) => setRow(r.key, { rate: e.target.value })} />
                    </Field>
                    <Field label="GST %">
                      <Input type="number" min="0" step="0.01" value={r.gst_rate_percent}
                             onChange={(e) => setRow(r.key, { gst_rate_percent: e.target.value })} />
                    </Field>
                    <Field label="Scope on this order" className="sm:col-span-2 lg:col-span-4"
                           hint="What is included at this rate — the sentence that settles the argument at billing.">
                      <Textarea rows={2} value={r.scope_text}
                                onChange={(e) => setRow(r.key, { scope_text: e.target.value })} />
                    </Field>
                  </div>
                )}

                {/* ---- what this line does, in figures ---- */}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-xs">
                  {l && (
                    <>
                      <span className="text-slate-500">Ordered <strong className="text-slate-700">{qty(l.qty_ordered)}</strong> {l.uom}</span>
                      <span className="text-slate-500">Allowed now <strong className="text-slate-700">{qty(l.qty_allowed)}</strong></span>
                      <span className="text-slate-500">Certified <strong className="text-slate-700">{qty(l.qty_certified)}</strong></span>
                      {q !== 0 && (
                        <span className="text-slate-500">
                          After this variation{' '}
                          <strong className={floorBust ? 'text-red-600' : 'text-amber-700'}>
                            {qty(n(l.qty_allowed) + q)}
                          </strong>
                        </span>
                      )}
                    </>
                  )}
                  {r.kind === 'NEW' && q > 0 && (
                    <span className="text-slate-500">
                      Becomes a line on the order at{' '}
                      <strong className="text-slate-700">Ordered 0, Allowed {qty(q)}</strong>
                    </span>
                  )}
                  <span className={amount < 0
                    ? 'ml-auto font-semibold text-emerald-700'
                    : 'ml-auto font-semibold text-slate-800'}>
                    {rupees(amount)}
                  </span>
                </div>

                {floorBust && (
                  <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      That takes the line below what has already been measured and
                      certified. The work is done; it has to be paid for.
                    </span>
                  </p>
                )}
              </div>
            )
          })}
        </div>

        {rows.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
            <p className="text-sm text-slate-500">
              {rows.filter((r) => num(r.qty_change) > 0).length} addition(s),{' '}
              {rows.filter((r) => num(r.qty_change) < 0).length} omission(s)
            </p>
            <p className="text-lg font-bold text-slate-900">
              {value < 0 && <Minus className="mr-1 inline h-4 w-4 text-emerald-700" />}
              {rupees(value)}
            </p>
          </div>
        )}
      </Card>

      {problems.length > 0 && (
        <Alert tone="warning" title="Not ready yet">
          <ul className="ml-4 list-disc space-y-1">
            {problems.map((p, i) => <li key={`${i}-${p.slice(0, 24)}`}>{p}</li>)}
          </ul>
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <button className="btn-secondary" disabled={busy || problems.length > 0}
                onClick={() => void save(false)}>
          Save as draft
        </button>
        <button className="btn-primary" disabled={busy || problems.length > 0}
                onClick={() => void save(true)}>
          <Send className="h-4 w-4" /> Save and submit
        </button>
      </div>

      <Confirm open={cancelling} title={`Cancel ${id}?`}
               tone="danger" confirmLabel="Cancel the variation"
               requireReason reasonLabel="Why is it being cancelled"
               message={<>It stays on the record as cancelled — the number was issued and
                 people may have been told about it — but the work order is free for
                 another variation straight away.</>}
               onConfirm={(why) => cancelIt(why)}
               onCancel={() => setCancelling(false)} />
    </div>
  )
}

// ===========================================================================
// ONE VARIATION IN DETAIL
// ===========================================================================
export function VariationDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  // Its own flag, not `busy`. Printing takes a second or two and must not grey
  // out Submit while it runs — they are unrelated actions.
  const [pdfBusy, setPdfBusy] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  // Names, not email addresses — management 01-09-2026. See src/lib/people.ts.
  const people = usePeople()

  const { data: rows, loading, refresh } = useQuery<VoRow>(
    `vo_${id}`,
    () => supabase.from('v_variation_register').select('*').eq('id', id),
    [id], { cacheable: false })

  const v = rows[0]

  const { data: lines } = useQuery<VoLine>(
    `vo_lines_${id}`,
    () => supabase.from('wo_variation_lines').select('*')
      .eq('variation_id', id).order('sl_no'),
    [id], { cacheable: false })

  const { data: siteRow } = useQuery<any>(
    `vo_site_${id}`,
    () => supabase.from('sites').select('id, project_manager_id')
      .eq('id', v?.site_id ?? '__none__'),
    [v?.site_id], { cacheable: false, enabled: Boolean(v?.site_id) })

  const { data: woLines } = useQuery<WoLineRow>(
    `vo_detail_wolines_${v?.wo_id ?? ''}`,
    () => supabase.from('v_wo_line_progress').select('*')
      .eq('wo_id', v?.wo_id ?? '__none__').order('sl_no'),
    [v?.wo_id], { cacheable: false, idField: 'wo_line_id', enabled: Boolean(v?.wo_id) })

  const lineOf = useMemo(() => {
    const m: Record<string, WoLineRow> = {}
    for (const l of woLines as WoLineRow[]) m[l.wo_line_id] = l
    return m
  }, [woLines])

  async function submit() {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_works_action', {
        p_doc_type: 'WO_VARIATION', p_doc_id: id, p_action: 'SUBMIT', p_comment: null,
      })
      if (error) throw new Error(error.message)
      toast.success(String(data ?? 'Submitted.')); refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not submit it')
    } finally { setBusy(false) }
  }

  async function cancelIt(why?: string) {
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_vo_cancel',
        { p_vo_id: id, p_reason: (why ?? '').trim() })
      if (error) throw new Error(error.message)
      toast.success('Cancelled.'); setCancelling(false); refresh()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not cancel it')
    } finally { setBusy(false) }
  }

  /**
   * THE PAPER THE CONTRACTOR IS HANDED.
   *
   * Until this existed an approved variation lived only inside the
   * application: the work order PDF prints the value as SIGNED and that figure
   * never moves, so the man doing the extra work had nothing saying it had been
   * ordered, at what rate, or what the contract was worth now.
   *
   * Deliberately NOT role-gated, on the same reasoning as the work order's
   * download: if a person may read this variation they may print it, and a
   * Project Manager who cannot hand his own contractor a copy would come
   * looking for somebody who can. The STATUS is the real control — the edge
   * function refuses anything that has not cleared the chain, because the sheet
   * carries the Project Head's seal, and the button is not drawn for those
   * statuses either.
   */
  async function downloadPdf() {
    setPdfBusy(true)
    try {
      await downloadFromFunction('generate-vo-pdf', { vo_id: id }, `${id}.pdf`)
      toast.success('Variation order PDF downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not generate the PDF')
    } finally { setPdfBusy(false) }
  }

  if (loading) return <Loading label="Reading the variation…" />
  if (!v) {
    return <EmptyState icon={<ListChecks className="h-8 w-8" />}
                       title="Not found"
                       message="No variation with that number, or it belongs to a site you cannot see." />
  }

  const mine = OPEN.includes(v.status) && serviceCan(profile?.role, 'raiseVariation')

  const D = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{value ?? '—'}</dd>
    </div>
  )

  return (
    <div className="space-y-4">
      <Link to="/service/variations"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All variations
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">{v.id}</h1>
          <p className="text-sm text-slate-600">
            Variation {v.variation_no} on{' '}
            <Link to={`/service/work-orders/${v.wo_id}`}
                  className="font-medium text-amber-700 hover:underline">{v.wo_id}</Link>
            {' — '}{v.wo_title}
          </p>
          <p className="mt-1 text-xs text-slate-500">{v.site_name} · {v.vendor_name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={v.status} />
          {v.status === 'APPROVED' && (
            <button className="btn-secondary btn-sm" disabled={pdfBusy}
                    onClick={() => void downloadPdf()}>
              <FileDown className="h-4 w-4" />
              {pdfBusy ? 'Preparing…' : 'Download PDF'}
            </button>
          )}
          {mine && (
            <>
              <Link to={`/service/variations/${v.id}/edit`} className="btn-secondary btn-sm">
                Edit
              </Link>
              <button className="btn-primary btn-sm" disabled={busy || n(v.line_count) === 0}
                      onClick={() => void submit()}>
                <Send className="h-4 w-4" /> Submit for approval
              </button>
              <button className="btn-ghost btn-sm text-red-600" disabled={busy}
                      onClick={() => setCancelling(true)}>
                <Ban className="h-4 w-4" /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <ApprovalChain docType="WO_VARIATION" docId={v.id} status={v.status}
                     currentStep={v.current_step}
                     sitePmId={siteRow[0]?.project_manager_id}
                     createdBy={v.created_by}
                     onChanged={refresh} />

      <Card title="Why it was raised">
        <p className="whitespace-pre-wrap text-sm text-slate-700">{v.reason}</p>
        {/* One column, three endings. rpc_works_action writes the RETURN
            reason and the REJECT reason into rejection_reason, and rpc_vo_cancel
            writes the cancellation into it too - so the label has to come from
            the status, or a cancelled variation reads as one somebody sent back
            for correction. */}
        {v.rejection_reason && (
          <p className={v.status === 'RETURNED'
            ? 'mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900'
            : 'mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-900'}>
            <strong>
              {v.status === 'RETURNED' ? 'Sent back'
                : v.status === 'CANCELLED' ? 'Cancelled'
                : v.status === 'REJECTED' ? 'Rejected' : 'Note'}:
            </strong>{' '}
            {v.rejection_reason}
          </p>
        )}
      </Card>

      <Card title="What it does to the order"
            subtitle="The signed contract value never changes. What the order is worth now is this plus every approved variation on it.">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <D label="Ordered value, as signed" value={rupees(v.wo_ordered_value)} />
          <D label="This variation" value={
            <span className={n(v.variation_value) < 0
              ? 'font-semibold text-emerald-700' : 'font-semibold'}>
              {rupees(v.variation_value)}
            </span>} />
          <D label="Share of the order"
             value={v.pct_of_order == null ? '—' : `${n(v.pct_of_order)}%`} />
          <D label={v.status === 'APPROVED'
                    ? 'The order is now worth' : 'The order would then be worth'}
             value={<span className="font-semibold">
               {rupees(v.status === 'APPROVED'
                 ? v.wo_revised_value
                 : n(v.wo_revised_value) + n(v.variation_value))}
             </span>} />
          <D label="Raised by" value={v.creator_email ? people.name({ email: v.creator_email }) : null} />
          <D label="Raised on" value={ddmmyyyy(v.date_created)} />
          <D label="Approved by" value={v.approver_email ? people.name({ email: v.approver_email }) : null} />
          <D label="Approved on" value={v.approval_date ? ddmmyyyy(v.approval_date) : null} />
        </dl>
      </Card>

      <Card title="The lines"
            subtitle="An item with no order line behind it becomes one, at quantity zero, the moment this is approved.">
        {lines.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            Nothing on it yet.{mine && ' Add the lines before submitting it.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>Item</th><th>What</th><th>UOM</th>
                  <th className="num">Change</th><th className="num">Rate</th>
                  <th className="num">Amount</th><th className="num">Allowed after</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const ol = l.wo_line_id ? lineOf[l.wo_line_id] : undefined
                  const isNew = !l.wo_line_id || (ol && n(ol.qty_ordered) === 0)
                  return (
                    <tr key={l.id}>
                      <td>{l.sl_no}</td>
                      <td className="max-w-[240px]">
                        {l.description}
                        {l.scope_text && (
                          <span className="block text-[11px] text-slate-400">{l.scope_text}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap text-xs">
                        {isNew
                          ? <span className="pill-blue">New item</span>
                          : n(l.qty_change) < 0
                            ? <span className="pill-green">Omission</span>
                            : <span className="pill-grey">Extra quantity</span>}
                      </td>
                      <td>{l.uom}</td>
                      <td className={n(l.qty_change) < 0 ? 'num text-emerald-700' : 'num'}>
                        {n(l.qty_change) > 0 ? '+' : ''}{qty(l.qty_change)}
                      </td>
                      <td className="num">{rupees(l.rate)}</td>
                      <td className={n(l.amount) < 0
                        ? 'num whitespace-nowrap font-semibold text-emerald-700'
                        : 'num whitespace-nowrap font-semibold'}>{rupees(l.amount)}</td>
                      <td className="num">{ol ? qty(ol.qty_allowed) : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {lines.length > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            &ldquo;Allowed after&rdquo; is what the Measurement Book will let the site
            certify once this is approved — ordered quantity plus every approved
            variation on that line.
          </p>
        )}
      </Card>

      {/* Above the audit trail and below the lines, because that is the order
          the work happens in: read what was varied, file what came back, and
          only then go looking at who did what and when. The card draws nothing
          at all unless the variation is APPROVED and the reader is one of the
          roles fn_may_see_vo_docs names. */}
      <VoSignedCopy voId={v.id} status={v.status} />

      <AuditTrail docType="WO_VARIATION" docId={v.id} />

      <Confirm open={cancelling} title={`Cancel ${v.id}?`}
               tone="danger" confirmLabel="Cancel the variation"
               requireReason reasonLabel="Why is it being cancelled"
               message={<>It stays on the record as cancelled, and {v.wo_id} is free for
                 another variation straight away.</>}
               onConfirm={(why) => cancelIt(why)}
               onCancel={() => setCancelling(false)} />
    </div>
  )
}
